/**
 * Coalesces concurrent `getAccessTokenSilently` calls into one in-flight
 * request, and retries when the Auth0 SPA SDK fails to acquire its
 * cross-tab/refresh Web Locks mutex.
 *
 * On app load, Apollo fires several GraphQL queries in parallel
 * (`AppContextProvider`), and each one asks Auth0 for a token via our
 * `setContext` link. If the token needs a silent refresh, every one of
 * those calls tries to acquire the same Auth0 lock at once, and the loser(s)
 * can throw `Lock not available` — surfacing as a fatal "Unable to load the
 * user" error even though the Auth0 session is perfectly valid.
 *
 * This wrapper: (1) ensures only one `getAccessTokenSilently` call is ever
 * in flight for the app's single audience/scope, so requests don't compete
 * for the lock, and (2) retries a short, bounded number of times if a lock
 * race still happens (e.g. another tab is mid-refresh).
 */

type GetAccessTokenSilently = () => Promise<string | undefined>;

/** Substrings of Auth0 SPA SDK errors worth a short retry. */
const RETRYABLE_ERROR_SUBSTRINGS = [
  "Lock not available",
  "Timeout when executing",
] as const;

const RETRY_DELAYS_MS = [250, 750, 1500] as const;

let inFlightTokenRequest: Promise<string | undefined> | null = null;

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

const isRetryableAuthError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return RETRYABLE_ERROR_SUBSTRINGS.some(substring => message.includes(substring));
};

const requestTokenWithRetries = async (
  getAccessTokenSilently: GetAccessTokenSilently,
): Promise<string | undefined> => {
  let lastError: unknown;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      return await getAccessTokenSilently();
    } catch (error) {
      lastError = error;
      const isLastAttempt = attempt === RETRY_DELAYS_MS.length;
      if (!isRetryableAuthError(error) || isLastAttempt) {
        throw error;
      }
      await sleep(RETRY_DELAYS_MS[attempt]);
    }
  }

  // Unreachable — the loop above always returns or throws — but keeps TS happy.
  throw lastError;
};

/**
 * Get an Auth0 access token, sharing a single in-flight request across
 * concurrent callers and retrying on transient lock-acquisition failures.
 */
export const getAccessTokenSingleFlight = (
  getAccessTokenSilently: GetAccessTokenSilently,
): Promise<string | undefined> => {
  if (inFlightTokenRequest) return inFlightTokenRequest;

  const request = requestTokenWithRetries(getAccessTokenSilently).finally(() => {
    if (inFlightTokenRequest === request) {
      inFlightTokenRequest = null;
    }
  });

  inFlightTokenRequest = request;
  return request;
};
